/**
 * The bottom dock — an element-major, Premiere-style timeline for one slide.
 *
 * Top: a narration strip (one block per step = a narration audio segment;
 * click to edit its audio, drag to reorder) and a continuous word ruler (click a
 * word to seek there). Below: grouped tracks — Reveals / Annotations / Camera —
 * ONE per element / annotation / camera move, each running the full slide width
 * with a single appear-and-stay clip you can drag anywhere (it re-homes to the
 * segment it lands on, snapping to words AND the seekbar). A scrub strip + global
 * red playhead let you drag to any ms and see that frame on the slide. Playback
 * controls live in the header. Fills its (adjustable) height with vertical scroll.
 */

import { Minus, Pause, Play, Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { RevealStep, Slide } from '@/lib/schema.types';
import { useEditorActions } from '@/store/editor.store';

import { GlobalRuler, RULER_H } from './GlobalRuler';
import { NarrationTrack, NARRATION_H } from './NarrationTrack';
import {
  bindClipToWord,
  buildGlobalWordStarts,
  buildTracks,
  moveClip,
  removeClip,
  resizeClip,
  type ItemKind,
  type TimelineClip,
  type TimelineTrack,
} from './items';
import { buildTimeline, fmtMs } from './playback';
import { ROW_H, TrackRow } from './TrackRow';
import type { ClipRef } from './clips';

const ZOOMS = [0.02, 0.035, 0.06, 0.1, 0.16, 0.26];
const DEFAULT_ZOOM = 3;
const ZOOM_KEY = 'teach.timeline.zoom';
const SCRUB_H = 16;
const GROUP_H = 16;
const GUTTER_W = 116;

const GROUPS: { kind: ItemKind; label: string }[] = [
  { kind: 'reveal', label: 'Reveals' },
  { kind: 'annotation', label: 'Annotations' },
  { kind: 'draw', label: 'Drawings' },
  { kind: 'spotlight', label: 'Spotlight' },
  { kind: 'camera', label: 'Camera' },
];

interface Props {
  slide: Slide;
  steps: RevealStep[];
  slideId: string;
  selectedClip: ClipRef | null;
  selectedStepId: string | null;
  playheadMs: number;
  totalMs: number;
  isPlaying: boolean;
  onTogglePlay: () => void;
  onScrub: (ms: number) => void;
  onSelectClip: (ref: ClipRef | null) => void;
  onSelectStep: (id: string) => void;
  onAddEmpty: () => void;
  onReorder: (ids: string[]) => void;
  onDeleteStep: (id: string) => void;
}

export function TimelineDock({
  slide,
  steps,
  slideId,
  selectedClip,
  selectedStepId,
  playheadMs,
  totalMs,
  isPlaying,
  onTogglePlay,
  onScrub,
  onSelectClip,
  onSelectStep,
  onAddEmpty,
  onReorder,
  onDeleteStep,
}: Props) {
  const actions = useEditorActions();
  const [zoom, setZoom] = useState(() => {
    const v = Number(localStorage.getItem(ZOOM_KEY));
    return Number.isInteger(v) && v >= 0 && v < ZOOMS.length ? v : DEFAULT_ZOOM;
  });
  const setZoomP = (n: number) => {
    const clamped = Math.max(0, Math.min(ZOOMS.length - 1, n));
    localStorage.setItem(ZOOM_KEY, String(clamped));
    setZoom(clamped);
  };
  const pxPerMs = ZOOMS[zoom];
  const trackRef = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState<{ clip: TimelineClip; kind: ItemKind; x: number; y: number } | null>(null);

  const { spans } = useMemo(() => buildTimeline(steps), [steps]);
  const tracks = useMemo(() => buildTracks(slide, steps, spans), [slide, steps, spans]);
  const wordStarts = useMemo(() => buildGlobalWordStarts(steps, spans), [steps, spans]);

  // Group tracks by kind; only non-empty groups show (so Annotations/Camera
  // appear exactly when there's something on them).
  const groups = useMemo(
    () => GROUPS.map((g) => ({ ...g, items: tracks.filter((t) => t.kind === g.kind) })).filter((g) => g.items.length > 0),
    [tracks],
  );

  const trackWidth = Math.max(1, Math.round(totalMs * pxPerMs));
  const rowsHeight = groups.reduce((acc, g) => acc + GROUP_H + ROW_H * g.items.length, 0);
  const headerH = SCRUB_H + NARRATION_H + RULER_H; // sticky band (scrub + narration + words)
  const contentH = headerH + rowsHeight;
  const playheadPx = Math.round(Math.max(0, Math.min(totalMs, playheadMs)) * pxPerMs);

  // ---- scrub ----
  const scrubToClientX = (clientX: number) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    onScrub(Math.max(0, Math.min(totalMs, (clientX - rect.left) / pxPerMs)));
  };
  const startScrub = (e: React.PointerEvent) => {
    e.preventDefault();
    scrubToClientX(e.clientX);
    const move = (ev: PointerEvent) => scrubToClientX(ev.clientX);
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // ---- item mutations ----
  const persist = (updates: RevealStep[]) => updates.forEach((s) => actions.upsertRevealStep(slideId, s));
  const selectClip = (clip: TimelineClip, kind: ItemKind) =>
    onSelectClip({ stepId: clip.ownerStepId, kind, id: clip.refId });
  const applyMove = (clip: TimelineClip, kind: ItemKind, g: number) => persist(moveClip(steps, clip, kind, g, spans));
  const applyResize = (clip: TimelineClip, kind: ItemKind, g: number) => persist(resizeClip(steps, clip, kind, g, spans));
  const deleteClip = (clip: TimelineClip, kind: ItemKind) => {
    const s = removeClip(steps, clip, kind);
    if (s) actions.upsertRevealStep(slideId, s);
    if (selectedClip?.id === clip.refId) onSelectClip(null);
  };
  // Add a reveal for an empty element track at a clicked time → synthesize a clip
  // anchored at the target step, then re-time it via moveClip.
  const addRevealAt = (track: TimelineTrack, g: number) => {
    if (!track.emptyElementId) return;
    const synthetic: TimelineClip = {
      refId: track.emptyElementId,
      ownerStepId: '',
      ownerStepIndex: -1,
      startMs: g,
      endMs: null,
      label: track.label,
      color: track.color,
      bound: false,
    };
    persist(moveClip(steps, synthetic, 'reveal', g, spans));
  };

  const menuStep = menu ? steps[menu.clip.ownerStepIndex] : undefined;
  const menuWords = menuStep?.audio?.words ?? [];

  const renderTrack = (t: TimelineTrack) => (
    <TrackRow
      key={t.key}
      track={t}
      pxPerMs={pxPerMs}
      total={totalMs}
      rowWidth={trackWidth}
      wordStarts={wordStarts}
      playheadMs={playheadMs}
      selectedRefId={selectedClip?.id ?? null}
      onSelect={(clip) => selectClip(clip, t.kind)}
      onMove={(clip, g) => applyMove(clip, t.kind, g)}
      onResize={(clip, g) => applyResize(clip, t.kind, g)}
      onAddAt={(g) => addRevealAt(t, g)}
      onMenu={(clip, x, y) => {
        selectClip(clip, t.kind);
        setMenu({ clip, kind: t.kind, x, y });
      }}
    />
  );

  return (
    <div className="flex h-full flex-col">
      {/* header — playback controls + step/zoom */}
      <div className="flex h-9 shrink-0 items-center justify-between border-b border-border-subtle px-3">
        <div className="flex items-center gap-2">
          <button
            onClick={onTogglePlay}
            disabled={steps.length === 0}
            className="grid h-6 w-6 place-items-center rounded-full bg-fg text-white transition hover:opacity-90 disabled:opacity-30"
            title={isPlaying ? 'Pause (space)' : 'Play (space)'}
          >
            {isPlaying ? <Pause size={12} /> : <Play size={12} className="ml-px" />}
          </button>
          <span className="font-mono text-[11px] tabular-nums text-fg-muted">
            {fmtMs(playheadMs)} <span className="text-fg-subtle">/ {fmtMs(totalMs)}</span>
          </span>
          <span className="ml-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-fg-subtle">Timeline</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onAddEmpty}
            className="inline-flex h-6 items-center gap-1 rounded border border-dashed border-border-strong px-2 text-[10px] font-semibold uppercase tracking-wider text-fg-muted hover:border-accent hover:text-accent"
          >
            <Plus size={11} /> Narration
          </button>
          <span className="mx-1 h-4 w-px bg-border" />
          <button
            onClick={() => setZoomP(zoom - 1)}
            disabled={zoom === 0}
            className="grid h-6 w-6 place-items-center rounded text-fg-muted hover:bg-bg-subtle disabled:opacity-30"
            title="Zoom out"
          >
            <Minus size={12} />
          </button>
          <button
            onClick={() => setZoomP(zoom + 1)}
            disabled={zoom === ZOOMS.length - 1}
            className="grid h-6 w-6 place-items-center rounded text-fg-muted hover:bg-bg-subtle disabled:opacity-30"
            title="Zoom in"
          >
            <Plus size={12} />
          </button>
        </div>
      </div>

      {/* body — gutter (sticky) + track, scrolls vertically + horizontally */}
      {steps.length === 0 ? (
        <div className="flex-1 p-6 text-center text-xs text-fg-muted">
          No narration yet. Use <span className="font-semibold">+ Narration</span> to add a segment,
          then drag element reveals onto the timeline.
        </div>
      ) : (
        <div className="flex-1 overflow-auto scroll-thin">
          <div className="flex" style={{ width: GUTTER_W + trackWidth, height: contentH }}>
            {/* gutter (sticky left) */}
            <div
              className="sticky left-0 z-30 shrink-0 border-r border-border bg-bg-subtle/40 text-[9px] font-medium text-fg-subtle"
              style={{ width: GUTTER_W }}
            >
              {/* sticky header labels — pinned at the top while tracks scroll */}
              <div className="sticky top-0 z-20 bg-bg-subtle">
                <div style={{ height: SCRUB_H }} className="border-b border-border-subtle/60" />
                <div style={{ height: NARRATION_H }} className="flex items-center border-b border-border-subtle px-2 uppercase tracking-wider">
                  Narration
                </div>
                <div style={{ height: RULER_H }} className="flex items-center border-b border-border-subtle px-2 uppercase tracking-wider">
                  Words
                </div>
              </div>
              {groups.map((g) => (
                <div key={g.kind}>
                  <div
                    style={{ height: GROUP_H }}
                    className="flex items-center bg-bg-subtle/70 px-2 text-[8px] font-bold uppercase tracking-[0.18em] text-fg-muted"
                  >
                    {g.label}
                  </div>
                  {g.items.map((t) => (
                    <div
                      key={t.key}
                      style={{ height: ROW_H }}
                      className="flex items-center gap-1 border-b border-border-subtle/70 px-2"
                      title={t.label}
                    >
                      <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: t.color }} />
                      <span className="truncate">{t.label}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* track */}
            <div ref={trackRef} className="relative shrink-0" style={{ width: trackWidth }}>
              {/* sticky header band — scrub + narration + words stay pinned */}
              <div className="sticky top-0 z-20 bg-white" style={{ height: headerH }}>
                <div
                  onPointerDown={startScrub}
                  className="cursor-ew-resize border-b border-border-subtle bg-bg-subtle/40 hover:bg-accent-soft/40"
                  style={{ height: SCRUB_H }}
                  title="Drag to scrub"
                />
                <NarrationTrack
                  steps={steps}
                  spans={spans}
                  pxPerMs={pxPerMs}
                  selectedStepId={selectedStepId}
                  onSelect={onSelectStep}
                  onReorder={onReorder}
                  onDelete={onDeleteStep}
                />
                <GlobalRuler steps={steps} spans={spans} total={totalMs} pxPerMs={pxPerMs} playheadMs={playheadMs} onSeek={onScrub} />
                {/* playhead — header segment */}
                <div className="pointer-events-none absolute top-0 z-30 w-px bg-red-500" style={{ left: playheadPx, height: headerH }}>
                  <span className="absolute -left-[3px] -top-0.5 h-1.5 w-1.5 rounded-full bg-red-500" />
                </div>
              </div>

              {/* scrolling tracks */}
              <div className="relative" style={{ height: rowsHeight }}>
                {groups.map((g) => (
                  <div key={g.kind}>
                    <div style={{ height: GROUP_H }} className="bg-bg-subtle/40" />
                    {g.items.map(renderTrack)}
                  </div>
                ))}
                {/* playhead — rows segment */}
                <div
                  className="pointer-events-none absolute top-0 z-30 w-px bg-red-500"
                  style={{ left: playheadPx, height: rowsHeight }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {menu && menuStep && (
        <ItemMenu
          x={menu.x}
          y={menu.y}
          words={menuWords}
          onBind={(wordIndex) => {
            persist(bindClipToWord(steps, menu.clip, menu.kind, spans, menu.clip.ownerStepIndex, wordIndex));
            setMenu(null);
          }}
          onClear={() => {
            persist(moveClip(steps, menu.clip, menu.kind, spans[menu.clip.ownerStepIndex]?.start ?? 0, spans));
            setMenu(null);
          }}
          onDelete={() => {
            deleteClip(menu.clip, menu.kind);
            setMenu(null);
          }}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
}

function ItemMenu({
  x,
  y,
  words,
  onBind,
  onClear,
  onDelete,
  onClose,
}: {
  x: number;
  y: number;
  words: { word: string; start: number }[];
  onBind: (wordIndex: number) => void;
  onClear: () => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    window.addEventListener('mousedown', onDoc);
    return () => window.removeEventListener('mousedown', onDoc);
  }, [onClose]);
  return (
    <div
      ref={ref}
      className="fixed z-[70] w-52 rounded-md border border-border bg-white py-1 text-[12px] shadow-floating"
      style={{ left: Math.min(x, window.innerWidth - 220), top: Math.min(y, window.innerHeight - 280) }}
    >
      <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-fg-subtle">Appear on word</div>
      {words.length === 0 ? (
        <div className="px-3 py-1.5 text-fg-subtle">No word timing in this segment.</div>
      ) : (
        <div className="max-h-44 overflow-y-auto scroll-thin">
          {words.map((w, i) => (
            <button
              key={i}
              onClick={() => onBind(i)}
              className="flex w-full items-center justify-between gap-2 px-4 py-1 text-left hover:bg-accent-soft"
            >
              <span className="truncate">{w.word}</span>
              <span className="font-mono text-[9px] text-fg-subtle">{w.start.toFixed(2)}s</span>
            </button>
          ))}
        </div>
      )}
      <div className="my-1 border-t border-border-subtle" />
      <button onClick={onClear} className="flex w-full px-3 py-1.5 text-left hover:bg-bg-subtle">
        Reset to segment start
      </button>
      <button onClick={onDelete} className="flex w-full px-3 py-1.5 text-left text-red-600 hover:bg-red-50">
        Delete
      </button>
    </div>
  );
}

export type { ClipRef } from './clips';
